import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import i18n from '../../i18n/i18n';

const BASE_URL = process.env.REACT_APP_BASE_URL || "https://backend.mystore.education/api";

const getToken = () => {
  // console.log('Searching for authentication token...');
  
  // البحث في sessionStorage أولاً
  const storedUser = sessionStorage.getItem("loggedUser");
  // console.log('sessionStorage loggedUser:', storedUser);
  
  if (storedUser) {
    try {
      const parsed = JSON.parse(storedUser);
      // console.log('Parsed sessionStorage user:', parsed);
      // البحث عن التوكن في أماكن مختلفة
      if (parsed.token) {
        // console.log('Found token in sessionStorage:', parsed.token);
        return parsed.token;
      }
      if (parsed.data?.token) {
        // console.log('Found token in sessionStorage.data:', parsed.data.token);
        return parsed.data.token;
      }
      if (parsed.result?.token) {
        // console.log('Found token in sessionStorage.result:', parsed.result.token);
        return parsed.result.token;
      }
      if (parsed.user?.token) {
        // console.log('Found token in sessionStorage.user:', parsed.user.token);
        return parsed.user.token;
      }
    } catch (error) {
      console.error('Error parsing storedUser:', error);
    }
  }
  
  // البحث في localStorage كبديل
  const localUser = localStorage.getItem("loggedUser");
  // console.log('localStorage loggedUser:', localUser);
  
  if (localUser) {
    try {
      const parsed = JSON.parse(localUser);
      // console.log('Parsed localStorage user:', parsed);
      if (parsed.token) {
        // console.log('Found token in localStorage:', parsed.token);
        return parsed.token;
      }
      if (parsed.data?.token) {
        // console.log('Found token in localStorage.data:', parsed.data.token);
        return parsed.data.token;
      }
      if (parsed.result?.token) {
        // console.log('Found token in localStorage.result:', parsed.result.token);
        return parsed.result.token;
      }
      if (parsed.user?.token) {
        // console.log('Found token in localStorage.user:', parsed.user.token);
        return parsed.user.token;
      }
    } catch (error) {
      console.error('Error parsing localUser:', error);
    }
  }
  
  // البحث عن التوكن مباشرة
  const directToken = sessionStorage.getItem("token") || localStorage.getItem("token");
  // console.log('Direct token search:', directToken);
  
  if (directToken) {
    // console.log('Found direct token:', directToken);
    return directToken;
  }
  
  console.error('No authentication token found in any location');
  // console.log('Available sessionStorage keys:', Object.keys(sessionStorage));
  // console.log('Available localStorage keys:', Object.keys(localStorage));
  return null;
};

// Helper function to get current language
const getCurrentLanguage = () => {
  return i18n.language || 'ar';
};

// Async thunks
export const getTeachers = createAsyncThunk(
  'teachers/getTeachers',
  async (params = {}, { rejectWithValue }) => {
    try {
      const page = typeof params === 'object' ? (params.page || 1) : params;
      const perPage = typeof params === 'object' ? (params.per_page || 10) : 10;
      const search = typeof params === 'object' ? (params.search || "") : "";
      const token = getToken();
      const currentLang = getCurrentLanguage();
      
      if (!token) {
        throw new Error('No authentication token found');
      }
      
      const response = await axios.get(`${BASE_URL}/dashboard/teachers?page=${page}&per_page=${perPage}&search=${encodeURIComponent(search)}`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Accept-Language": currentLang,
          "lang": currentLang,
        },
      });
      return response.data;
    } catch (error) {
      console.error('Teachers API Error:', error);
      console.error('Error response:', error.response);
      return rejectWithValue(error.response?.data?.message || 'حدث خطأ في جلب بيانات المعلمين');
    }
  }
);
export const getAllTeachers = createAsyncThunk(
  'teachers/getAllTeachers',
  async (_, { rejectWithValue }) => {
    try {
      const token = getToken();
      const currentLang = getCurrentLanguage();
      
      if (!token) {
        throw new Error('No authentication token found');
      }
      
      const response = await axios.get(`${BASE_URL}/dashboard/all/teachers`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Accept-Language": currentLang,
          "lang": currentLang,
        },
      });
      return response.data;
    } catch (error) {
      console.error('Teachers API Error:', error);
      console.error('Error response:', error.response);
      return rejectWithValue(error.response?.data?.message || 'حدث خطأ في جلب بيانات المعلمين');
    }
  }
);

export const getSettingsTeachers = createAsyncThunk(
  'teachers/getSettingsTeachers',
  async (_, { rejectWithValue }) => {
    try {
      const token = getToken();
      const currentLang = getCurrentLanguage();
      
      if (!token) {
        throw new Error('No authentication token found');
      }
      
      const response = await axios.get(`${BASE_URL}/dashboard/settings/teachers`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Accept-Language": currentLang,
          "lang": currentLang,
        },
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'حدث خطأ في جلب بيانات المعلمين');
    }
  }
);

export const addTeacher = createAsyncThunk(
  'teachers/addTeacher',
  async (teacherData, { rejectWithValue }) => {
    try {
      const token = getToken();
      const currentLang = getCurrentLanguage();
      
      if (!token) {
        throw new Error('No authentication token found');
      }
      
      // إنشاء FormData
      const formData = new FormData();
      formData.append('name_en', teacherData.name_en);
      formData.append('name_ar', teacherData.name_ar);
      formData.append('status', teacherData.status);
      formData.append('subject_id', teacherData.subject_id);
      
      // إضافة الصفوف
      if (teacherData.grades && teacherData.grades.length > 0) {
        teacherData.grades.forEach(gradeId => {
          formData.append('grades[]', gradeId);
        });
      }
      
      // إضافة المنتجات
      if (teacherData.products && teacherData.products.length > 0) {
        teacherData.products.forEach(productId => {
          formData.append('products[]', productId);
        });
      }
      
      const response = await axios.post(`${BASE_URL}/dashboard/teachers`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Accept-Language": currentLang,
          "lang": currentLang,
          'Content-Type': 'multipart/form-data',
        },
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'حدث خطأ في إضافة المعلم');
    }
  }
);

export const updateTeacher = createAsyncThunk(
  'teachers/updateTeacher',
  async ({ id, teacherData }, { rejectWithValue }) => {
    try {
      const token = getToken();
      const currentLang = getCurrentLanguage();
      
      if (!token) {
        throw new Error('No authentication token found');
      }
      
      // إنشاء FormData
      const formData = new FormData();
      formData.append('name_en', teacherData.name_en);
      formData.append('name_ar', teacherData.name_ar);
      formData.append('status', teacherData.status);
      formData.append('subject_id', teacherData.subject_id);
      
      // إضافة الصفوف
      if (teacherData.grades && teacherData.grades.length > 0) {
        teacherData.grades.forEach(gradeId => {
          formData.append('grades[]', gradeId);
        });
      }
      
      // إضافة المنتجات
      if (teacherData.products && teacherData.products.length > 0) {
        teacherData.products.forEach(productId => {
          formData.append('products[]', productId);
        });
      }
      
      const response = await axios.post(`${BASE_URL}/dashboard/teachers/${id}/update`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Accept-Language": currentLang,
          "lang": currentLang,
          'Content-Type': 'multipart/form-data',
        },
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'حدث خطأ في تحديث المعلم');
    }
  }
);

export const deleteTeacher = createAsyncThunk(
  'teachers/deleteTeacher',
  async (id, { rejectWithValue }) => {
    try {
      const token = getToken();
      const currentLang = getCurrentLanguage();
      
      if (!token) {
        throw new Error('No authentication token found');
      }
      
      const response = await axios.delete(`${BASE_URL}/dashboard/teachers/${id}`, {
        headers: {
          
          Authorization: `Bearer ${token}`,
          "Accept-Language": currentLang,
          "lang": currentLang,
        },
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'حدث خطأ في حذف المعلم');
    }
  }
);

const initialState = {
  teachers: [],
  all_teachers: [],
  settingsTeachers: [],
  pagination: null,
  loading: false,
  error: null,
  success: null,
};

const teachersSlice = createSlice({
  name: 'teachers',
  initialState,
  reducers: {
    clearState: (state) => {
      state.error = null;
      state.success = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Get Teachers
      .addCase(getTeachers.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getTeachers.fulfilled, (state, action) => {
        state.loading = false;
        // console.log('Teachers API Response:', action.payload);
        state.teachers = action.payload.data;
        state.pagination = action.payload.meta;
        state.error = null;
      })
      .addCase(getTeachers.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(getAllTeachers.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getAllTeachers.fulfilled, (state, action) => {
        state.loading = false;
        state.all_teachers = action.payload.data;
        state.error = null;
      })
      .addCase(getAllTeachers.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Add Teacher
      .addCase(addTeacher.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(addTeacher.fulfilled, (state, action) => {
        state.loading = false;
        state.success = action.payload.message || 'تم إضافة المعلم بنجاح';
        state.error = null;
      })
      .addCase(addTeacher.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Update Teacher
      .addCase(updateTeacher.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateTeacher.fulfilled, (state, action) => {
        state.loading = false;
        state.success = action.payload.message || 'تم تحديث المعلم بنجاح';
        state.error = null;
      })
      .addCase(updateTeacher.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Delete Teacher
      .addCase(deleteTeacher.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteTeacher.fulfilled, (state, action) => {
        state.loading = false;
        state.success = action.payload.message || 'تم حذف المعلم بنجاح';
        state.error = null;
      })
      .addCase(deleteTeacher.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(getSettingsTeachers.pending, (state) => {
        state.loading = true;
      })
      .addCase(getSettingsTeachers.fulfilled, (state, action) => {
        state.loading = false;
        state.settingsTeachers = action.payload.data;
      })
      .addCase(getSettingsTeachers.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const { clearState } = teachersSlice.actions;
export default teachersSlice.reducer;
